import { memo, useState } from 'react';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { reducedMotion as reducedMotionQuery } from '@/shared/ui/theme';
import * as s from './LuminariumBackdrop.css';
import { CyberGridGL } from './CyberGridGL';
import { hologramSceneStore } from './hologramSceneStore';

// --- Props ---

export interface LuminariumBackdropProps {
  /** 광원장 전체 강도 (0~1, 기본 0.5) */
  intensity?: number;
  /** Auth scene fade-in 게이팅 — phase < 2면 opacity 0 */
  phase?: number;
  /** opacity 직접 override (default 1) */
  opacity?: number;
}

// WebGL 지원은 앱이 떠 있는 동안 바뀌지 않는다 — 한 번만 확인하고 확인에 쓴 컨텍스트는 바로 푼다.
// Why: 마운트마다 새 캔버스로 확인하면 풀리지 않은 컨텍스트가 GC 전까지 GPU 자원과 브라우저
// 컨텍스트 상한(~16개)을 차지한다.
let webglSupported: boolean | undefined;
function supportsWebGL(): boolean {
  if (webglSupported === undefined) {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') ?? c.getContext('webgl');
    webglSupported = !!gl;
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  }
  return webglSupported;
}

// --- Component ---
//
// AXIOM Shell L0 — Luminarium(광원장).
// cathedral nave + 3 stair + altar + 8 Lichtdom pillar + hologram modal projection.
// 모든 main 페이지의 backdrop. WebGL 실패 시 CSS fallback grid로 graceful degrade.

export const LuminariumBackdrop = memo(
  ({ intensity = 0.5, phase, opacity = 1 }: LuminariumBackdropProps) => {
    const prefersReducedMotion = useMediaQuery(reducedMotionQuery);
    const [webglFailed] = useState(() => typeof document === 'undefined' || !supportsWebGL());
    // 로그아웃이 설계도까지 내려 두고 넘어왔다 — 다시 투명에서 밝히면 설계도가 꺼졌다 켜진다
    const [arrivedConcealed] = useState(() => hologramSceneStore.reveal === 0);

    const gated = phase !== undefined && phase < 2 && !arrivedConcealed;
    const finalOpacity = gated ? 0 : opacity;
    const opacityTransition =
      prefersReducedMotion || arrivedConcealed ? 'none' : 'opacity 2s ease-out';

    return (
      <div
        className={s.luminariumContainer}
        aria-hidden="true"
        style={{
          opacity: finalOpacity,
          transition: opacityTransition,
        }}
      >
        {webglFailed ? (
          <div className={s.luminariumFallback} />
        ) : (
          <CyberGridGL intensity={intensity} />
        )}
      </div>
    );
  }
);
